import { Component, Input, OnInit, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ProtocolService } from '@artemis-software/wr-api';
import { loader } from '@/utils/signals/loader';

@Component({
  standalone: false,
  selector: 'wr-protocol-share-link-dialog',
  templateUrl: './protocol-share-link-dialog.component.html',
  styleUrls: ['./protocol-share-link-dialog.component.scss'],
})
export class ProtocolShareLinkDialogComponent implements OnInit {
  @Input() protocolId!: string;
  @Input() existingShareLinkId?: string;
  @Input() existingShareLinkExpiration?: string;

  readonly loading = loader();
  durations: number[] = [1, 7, 30, 90, 365];
  selectedDuration = signal(30);

  readonly shareLinkId = signal<string | undefined>(undefined);
  readonly shareLinkExpiration = signal<string | undefined>(undefined);

  constructor(private readonly protocolService: ProtocolService) {}

  ngOnInit(): void {
    if (this.existingShareLinkId) {
      this.shareLinkId.set(this.existingShareLinkId);
    }
    if (this.existingShareLinkExpiration) {
      this.shareLinkExpiration.set(this.existingShareLinkExpiration);
    }
  }

  get shareLink(): string {
    const id = this.shareLinkId();
    return id ? `${window.location.origin}/protocol-share/${id}` : '';
  }

  async generateShareLink(): Promise<void> {
    if (this.loading()) return;
    const loaderItem = this.loading.add();
    loaderItem.set(true);
    try {
      const id = await firstValueFrom(
        this.protocolService.generateShareLink(this.protocolId, { durationDays: this.selectedDuration() }),
      );
      this.shareLinkId.set(id);
      this.shareLinkExpiration.set(new Date(Date.now() + this.selectedDuration() * 86_400_000).toISOString());
    } finally {
      loaderItem.set(false);
      loaderItem.stop();
    }
  }

  async revokeShareLink(): Promise<void> {
    if (this.loading()) return;
    const loaderItem = this.loading.add();
    loaderItem.set(true);
    try {
      await firstValueFrom(this.protocolService.revokeShareLink(this.protocolId));
      this.shareLinkId.set(undefined);
      this.shareLinkExpiration.set(undefined);
    } finally {
      loaderItem.set(false);
      loaderItem.stop();
    }
  }

  copyToClipboard(link: string): void {
    navigator.clipboard.writeText(link).catch(err => console.error(err));
  }

  openLink(link: string): void {
    if (link) window.open(link, '_blank');
  }
}
